import {
  ClerkOfflineError,
  isClerkAPIResponseError,
  isClerkRuntimeError,
} from "@clerk/react/errors"
import { isAxiosError } from "axios"
import type { TokenGetter } from "./clerk-token-provider"

export type TokenFailureKind = "authentication" | "temporary" | "configuration"

export class TokenRequestError extends Error {
  discardAccess = false
  readonly status: number
  readonly data: { status: "error"; code: string; message: string }

  constructor(
    readonly kind: TokenFailureKind,
    readonly code: string,
    readonly retryAfterSeconds?: number,
    responseStatus?: number,
  ) {
    const message =
      kind === "authentication"
        ? "Please sign in again to verify your access."
        : "We could not verify your access. Please try again."
    super(message)
    this.name = "TokenRequestError"
    // No API request exists for a failed token acquisition. Preserve that distinction.
    this.status = responseStatus ?? (kind === "authentication" ? 401 : 0)
    this.data = { status: "error", code, message }
  }
}

function normalizeTokenError(error: unknown): TokenRequestError {
  if (error instanceof TokenRequestError) return error
  if (ClerkOfflineError.is(error)) return new TokenRequestError("temporary", "CLERK_OFFLINE")
  if (isClerkRuntimeError(error) && error.code === "clerk_runtime_load_timeout")
    return new TokenRequestError("temporary", "CLERK_NOT_READY")
  if (isAxiosError(error) && !error.response)
    return new TokenRequestError("temporary", "NETWORK_REQUEST_FAILED")
  if (isClerkAPIResponseError(error)) {
    const kind =
      error.status === 401
        ? "authentication"
        : [408, 429, 500, 502, 503, 504].includes(error.status)
          ? "temporary"
          : "configuration"
    // Log only machine codes and status. Provider messages/payloads can contain credentials.
    console.warn("[Authentication] Token request failed", {
      status: error.status,
      codes: error.errors.map(({ code }) => code),
    })
    return new TokenRequestError(kind, "CLERK_TOKEN_REQUEST_FAILED", error.retryAfter)
  }
  return new TokenRequestError("configuration", "CLERK_TOKEN_UNAVAILABLE")
}

const refreshes = new WeakMap<TokenGetter, Promise<string | null>>()

export async function clerkRequest<T extends { status: number }>(
  getToken: TokenGetter,
  send: (token: string) => Promise<T>,
  method: string,
  isCurrent: () => boolean = () => true,
): Promise<T> {
  const checkIdentity = () => {
    if (!isCurrent()) throw new TokenRequestError("authentication", "AUTHENTICATION_CHANGED")
  }
  const token = async (refresh: boolean): Promise<string> => {
    checkIdentity()
    try {
      let result: string | null
      if (refresh) {
        let pending = refreshes.get(getToken)
        if (!pending) {
          pending = getToken({ template: "vivid", skipCache: true })
          refreshes.set(getToken, pending)
          void pending
            .finally(() => {
              if (refreshes.get(getToken) === pending) refreshes.delete(getToken)
            })
            .catch(() => {})
        }
        result = await pending
      } else {
        result = await getToken({ template: "vivid" })
      }
      checkIdentity()
      if (!result) throw new TokenRequestError("authentication", "AUTHENTICATION_REQUIRED")
      return result
    } catch (error) {
      throw normalizeTokenError(error)
    }
  }

  const firstToken = await token(false)
  checkIdentity()
  const response = await send(firstToken)
  checkIdentity()
  // Read-only replay cannot duplicate a saved report, upload, or workflow action.
  if (response.status === 401 && ["GET", "HEAD"].includes(method.toUpperCase())) {
    try {
      const freshToken = await token(true)
      checkIdentity()
      const retried = await send(freshToken)
      checkIdentity()
      // A response is not successful recovery merely because the transport resolved it.
      // 401/403 already replace access through their normal classification.
      if ((retried.status < 200 || retried.status >= 300) && ![401, 403].includes(retried.status)) {
        throw new TokenRequestError(
          [408, 429, 500, 502, 503, 504].includes(retried.status) ? "temporary" : "configuration",
          "AUTHENTICATION_RECOVERY_FAILED",
          undefined,
          retried.status,
        )
      }
      return retried
    } catch (error) {
      const failure = normalizeTokenError(error)
      failure.discardAccess = true
      throw failure
    }
  }
  return response
}
